import {
  CronExpression,
  getDailyScheduleTime,
  streamSpecifierToEventStreamIds,
} from '@cotera/sdk/core';

export const STANDARD_SCHEDULES = ['hourly', 'daily', 'never'] as const;
export type StandardSchedule = (typeof STANDARD_SCHEDULES)[number];
export type Schedule = StandardSchedule | { cron: string };

const SILLY_CRON_OPTIONS = [
  'EVERY_SECOND',
  'EVERY_5_SECONDS',
  'EVERY_10_SECONDS',
  'EVERY_30_SECONDS',
  'EVERY_MINUTE',
] as const satisfies (keyof typeof CronExpression)[];

export const SENSIBLE_CRON_OPTIONS = Object.entries(CronExpression).filter(
  ([k]) => {
    return !SILLY_CRON_OPTIONS.includes(k as any);
  }
);

export const valueFromSchedule = (schedule: Schedule) => {
  switch (schedule) {
    case 'hourly':
      return 'hourly';
    case 'daily':
      return 'daily';
    case 'never':
      return 'never';
    default: {
      const value = SENSIBLE_CRON_OPTIONS.find(([_, v]) => v === schedule.cron);
      if (value !== undefined) {
        return value[0];
      }
      return schedule.cron;
    }
  }
};

export const scheduleLabel = (schedule: string) => {
  return schedule
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

/**
 * Helper function to get schedule context from event stream
 * Uses the same logic as the backend to ensure consistency
 */
export const getScheduleContextFromEventStream = (
  orgId: string,
  eventStream: any // EventStreamDefinition from @cotera/sdk/core
): { orgId: string; entityName: string; streamId: string } | undefined => {
  if (!eventStream?.specifier) {
    return undefined;
  }

  const { entityName, streamId } = streamSpecifierToEventStreamIds(
    eventStream.specifier
  );

  return {
    orgId,
    entityName,
    streamId,
  };
};

export const scheduleDescription = (
  when: Schedule,
  type: 'agent' | 'tool',
  context?: { orgId: string; entityName: string; streamId: string }
) => {
  if (valueFromSchedule(when) === 'never') {
    return `This ${type} will not run automatically`;
  }

  if (valueFromSchedule(when) === 'daily' && context) {
    // Calculate the specific time for daily schedules
    const { hour, minute } = getDailyScheduleTime(
      context.orgId,
      context.entityName,
      context.streamId
    );

    // Display UTC time directly (no timezone conversion)
    const displayMinute = minute.toString().padStart(2, '0');

    return `This ${type} will run daily at ${hour}:${displayMinute} UTC`;
  }

  return `This ${type} will run ${scheduleLabel(
    valueFromSchedule(when)
  ).toLowerCase()}`;
};
